//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import { useCallback, useEffect, useRef } from 'react';
import { useActiveLoopContext } from '../ActiveLoopContext';
import { useTeamspaceContext } from '../TeamspaceContext';
import {
  ContextPayload,
  buildContextMessage,
  parseContextMessage,
  sendMessageToIframe,
} from '../utils/contextMessaging';

export interface UseContextBridgeArgs {
  getIframe?: () => HTMLIFrameElement | null;
  allowedOrigins?: string[];
}

export interface UseContextBridge {
  getCurrentPayload: () => ContextPayload | null;
  broadcastContext: () => void;
  requestContext: () => void;
}

export function useContextBridge(
  args: UseContextBridgeArgs = {},
): UseContextBridge {
  const { getIframe, allowedOrigins = [] } = args;

  const {
    activeLoopId,
    activeLoopName,
    activeTeamspaceId,
    setLoopContext,
    setTeamspaceContext,
  } = useActiveLoopContext();

  const { teamspaceId: teamspaceCtxId } = useTeamspaceContext();

  // Use refs for values accessed inside the message listener to avoid
  // tearing down / re-adding the listener on every context change.
  const activeLoopIdRef = useRef(activeLoopId);
  const activeLoopNameRef = useRef(activeLoopName);
  const activeTeamspaceIdRef = useRef(activeTeamspaceId);
  const teamspaceCtxIdRef = useRef(teamspaceCtxId);
  const setLoopContextRef = useRef(setLoopContext);
  const setTeamspaceContextRef = useRef(setTeamspaceContext);
  const getIframeRef = useRef(getIframe);
  const allowedOriginsRef = useRef(allowedOrigins);

  useEffect(() => {
    activeLoopIdRef.current = activeLoopId;
  }, [activeLoopId]);
  useEffect(() => {
    activeLoopNameRef.current = activeLoopName;
  }, [activeLoopName]);
  useEffect(() => {
    activeTeamspaceIdRef.current = activeTeamspaceId;
  }, [activeTeamspaceId]);
  useEffect(() => {
    teamspaceCtxIdRef.current = teamspaceCtxId;
  }, [teamspaceCtxId]);
  useEffect(() => {
    setLoopContextRef.current = setLoopContext;
  }, [setLoopContext]);
  useEffect(() => {
    setTeamspaceContextRef.current = setTeamspaceContext;
  }, [setTeamspaceContext]);
  useEffect(() => {
    getIframeRef.current = getIframe;
  }, [getIframe]);
  useEffect(() => {
    allowedOriginsRef.current = allowedOrigins;
  }, [allowedOrigins]);

  const getCurrentPayload = useCallback((): ContextPayload | null => {
    const tsId = activeTeamspaceIdRef.current || teamspaceCtxIdRef.current;
    if (!tsId) return null;
    return {
      teamspaceId: tsId,
      loopId: activeLoopIdRef.current,
      loopName: activeLoopNameRef.current,
      level: activeLoopIdRef.current ? 'loop' : 'teamspace',
    };
  }, []);

  const broadcastContext = useCallback(() => {
    const payload = getCurrentPayload();
    if (!payload) return;
    const iframe = getIframeRef.current?.();
    if (!iframe) return;
    try {
      const message = buildContextMessage('CONTEXT_UPDATE', payload);
      sendMessageToIframe(iframe, message, allowedOriginsRef.current);
    } catch {
      // No valid payload to send — silently ignore.
    }
  }, [getCurrentPayload]);

  const requestContext = useCallback(() => {
    const payload = getCurrentPayload();
    if (!payload) return;
    const iframe = getIframeRef.current?.();
    if (!iframe) return;
    try {
      const message = buildContextMessage('CONTEXT_REQUEST', payload);
      sendMessageToIframe(iframe, message, allowedOriginsRef.current);
    } catch {
      // No valid payload to send — silently ignore.
    }
  }, [getCurrentPayload]);

  // Single message listener — registered once on mount, removed on unmount.
  const listenerAttached = useRef(false);

  useEffect(() => {
    if (listenerAttached.current) return;
    listenerAttached.current = true;

    const handleMessage = (event: MessageEvent) => {
      const msg = parseContextMessage(event, allowedOriginsRef.current);
      if (!msg) return;

      // If getIframe is provided, only accept messages from the owned iframe.
      if (getIframeRef.current) {
        const iframe = getIframeRef.current();
        if (iframe && event.source !== iframe.contentWindow) return;
      }

      if (msg.type === 'CONTEXT_UPDATE') {
        const { teamspaceId, loopId, loopName } = msg.payload;
        if (loopId) {
          setLoopContextRef.current(loopId, loopName ?? loopId, teamspaceId);
        } else {
          setTeamspaceContextRef.current(teamspaceId);
        }
      } else if (msg.type === 'CONTEXT_REQUEST') {
        // Respond with current context.
        const payload = getCurrentPayload();
        if (!payload) return;
        const iframe = getIframeRef.current?.();
        if (!iframe) return;
        try {
          const response = buildContextMessage('CONTEXT_UPDATE', payload);
          sendMessageToIframe(iframe, response, allowedOriginsRef.current);
        } catch {
          // Ignore build errors.
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      listenerAttached.current = false;
      window.removeEventListener('message', handleMessage);
    };
  }, [getCurrentPayload]);

  return { getCurrentPayload, broadcastContext, requestContext };
}
